import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import workOrdersService from '../../services/workOrdersService';
import CreateWorkOrderModal from './CreateWorkOrderModal';
import WorkOrderDetailsModal from './WorkOrderDetailsModal';
import './Dashboard.css';

const WORK_ORDER_STATUSES = ['Draft', 'Submitted', 'In Production', 'Completed', 'Delivered'];

const PRIORITY_CONFIG = {
  rush: { color: '#ef4444', bg: '#fef2f2', label: 'Rush' },
  high: { color: '#f59e0b', bg: '#fffbeb', label: 'High' },
  normal: { color: '#3b82f6', bg: '#eff6ff', label: 'Normal' },
  low: { color: '#6b7280', bg: '#f9fafb', label: 'Low' }
};

const STATUS_CONFIG = {
  'Draft': { color: '#6b7280', icon: '📝' },
  'Submitted': { color: '#6366f1', icon: '📥' },
  'In Production': { color: '#f59e0b', icon: '🖨️' },
  'Completed': { color: '#22c55e', icon: '✅' },
  'Delivered': { color: '#3b82f6', icon: '📦' }
};

const Dashboard = () => {
  const { hasPermission, hasRole } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);

  // Mobile filters toggle
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Check permissions
  const canWrite = hasPermission('work-orders:write') || hasRole('admin');
  const canAdmin = hasPermission('work-orders:admin') || hasRole('admin');

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (searchTerm) params.search = searchTerm;

      const response = await workOrdersService.getWorkOrders(params);
      setJobs(response.jobs || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching work orders:', err);
      setError(err.response?.data?.message || 'Failed to fetch work orders');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, searchTerm]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await workOrdersService.getStats();
      setStats(response.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, [fetchJobs, fetchStats]);

  // Handle job click
  const handleJobClick = (job) => {
    setSelectedJob(job);
    setIsDetailsModalOpen(true);
  };

  // Handle edit
  const handleEditJob = (job) => {
    setEditingJob(job);
    setIsCreateModalOpen(true);
  };

  // Handle modal success
  const handleModalSuccess = () => {
    fetchJobs();
    fetchStats();
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return '$' + parseFloat(amount).toFixed(2);
  };

  // Check if job is overdue
  const isOverdue = (job) => {
    if (!job.due_date || job.status === 'Completed' || job.status === 'Delivered') return false;
    return new Date(job.due_date) < new Date();
  };

  // Render priority badge
  const renderPriorityBadge = (priority) => {
    const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
    return (
      <span
        className="wo-priority-badge"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          borderColor: config.color
        }}
      >
        {config.label}
      </span>
    );
  };

  // Render status indicator
  const renderStatusIndicator = (status) => {
    const config = STATUS_CONFIG[status] || { color: '#6b7280', icon: '•' };
    return (
      <div className="wo-status-indicator-wrapper">
        <span
          className="wo-status-dot"
          style={{ backgroundColor: config.color }}
        />
        <span className="wo-status-label">{status}</span>
      </div>
    );
  };

  // Stat card component
  const StatCard = ({ title, value, icon, color }) => (
    <div className="wo-stat-card" style={{ '--accent-color': color }}>
      <div className="wo-stat-icon" style={{ backgroundColor: `${color}15`, color }}>
        {icon}
      </div>
      <div className="wo-stat-content">
        <span className="wo-stat-label">{title}</span>
        <span className="wo-stat-value" style={{ color }}>{value}</span>
      </div>
    </div>
  );

  return (
    <div className="wo-dashboard">
      {/* Page Header */}
      <div className="wo-page-header">
        <div className="wo-page-header-content">
          <div className="wo-page-title-section">
            <div className="wo-page-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div>
              <h1>Work Orders</h1>
              <p className="wo-page-subtitle">Kerr Screen printing work order management</p>
            </div>
          </div>
          <div className="wo-page-actions">
            <Link to="/work-orders/print-queue" className="wo-btn-secondary">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 6 2 18 2 18 9"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              <span>Print Queue</span>
            </Link>
            {canWrite && (
              <button
                className="wo-btn-primary"
                onClick={() => {
                  setEditingJob(null);
                  setIsCreateModalOpen(true);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                <span>New Work Order</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      {stats && (
        <div className="wo-stats-section">
          <div className="wo-stats-grid">
            <StatCard
              title="Total Orders"
              value={stats.total}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
              }
              color="#6366f1"
            />
            <StatCard
              title="In Production"
              value={stats.byStatus?.['In Production'] || 0}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9"/>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                </svg>
              }
              color="#f59e0b"
            />
            <StatCard
              title="Due Soon"
              value={stats.dueSoon}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              }
              color="#3b82f6"
            />
            <StatCard
              title="Overdue"
              value={stats.overdue}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              }
              color="#ef4444"
            />
          </div>
        </div>
      )}

      {/* Controls Section */}
      <div className="wo-controls-section">
        <div className="wo-controls-row">
          {/* Mobile Filter Toggle */}
          <button
            className="wo-mobile-filter-toggle"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            <span>Filters</span>
          </button>

          {/* Filters */}
          <div className={`wo-filters-container ${showMobileFilters ? 'show' : ''}`}>
            <div className="wo-search-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="wo-search-input"
              />
            </div>
            <div className="wo-filter-group">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="wo-filter-select"
              >
                <option value="all">All Status</option>
                {WORK_ORDER_STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="wo-filter-select"
              >
                <option value="all">All Priority</option>
                <option value="rush">Rush</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="wo-main-content">
        {loading ? (
          <div className="wo-loading-state">
            <div className="wo-loading-spinner" />
            <p>Loading work orders...</p>
          </div>
        ) : error ? (
          <div className="wo-error-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <h3>Error Loading Work Orders</h3>
            <p>{error}</p>
            <button onClick={fetchJobs} className="wo-btn-retry">Try Again</button>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="wo-table-container">
              {jobs.length === 0 ? (
                <div className="wo-empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="12" y1="18" x2="12" y2="12"/>
                    <line x1="9" y1="15" x2="15" y2="15"/>
                  </svg>
                  <h3>No Work Orders Found</h3>
                  <p>Get started by creating your first work order</p>
                  {canWrite && (
                    <button className="wo-btn-primary" onClick={() => setIsCreateModalOpen(true)}>
                      Create Work Order
                    </button>
                  )}
                </div>
              ) : (
                <table className="wo-jobs-table">
                  <thead>
                    <tr>
                      <th>WO #</th>
                      <th>Customer</th>
                      <th>Invoice #</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Due Date</th>
                      <th>Total Due</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(job => (
                      <tr
                        key={job.id}
                        className={`${isOverdue(job) ? 'wo-overdue-row' : ''} ${job.priority === 'rush' ? 'wo-rush-row' : ''}`}
                        onClick={() => handleJobClick(job)}
                      >
                        <td>
                          <span className="wo-job-number-cell">{job.work_order_number}</span>
                        </td>
                        <td>
                          <div className="wo-customer-cell">
                            <span className="wo-customer-name">{job.customer}</span>
                          </div>
                        </td>
                        <td>
                          <span className="wo-invoice-cell">{job.invoice_number || '-'}</span>
                        </td>
                        <td>{renderStatusIndicator(job.status)}</td>
                        <td>{renderPriorityBadge(job.priority)}</td>
                        <td>
                          <span className={`wo-date-cell ${isOverdue(job) ? 'overdue' : ''}`}>
                            {formatDate(job.due_date)}
                          </span>
                        </td>
                        <td>
                          <span className="wo-amount-cell">{formatCurrency(job.total_due)}</span>
                        </td>
                        <td>
                          <button className="wo-btn-view" onClick={(e) => { e.stopPropagation(); handleJobClick(job); }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Mobile Card List */}
            <div className="wo-mobile-card-list">
              {jobs.length === 0 ? (
                <div className="wo-empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <h3>No Work Orders Found</h3>
                </div>
              ) : (
                jobs.map(job => (
                  <div
                    key={job.id}
                    className={`wo-mobile-job-card ${isOverdue(job) ? 'overdue' : ''}`}
                    onClick={() => handleJobClick(job)}
                  >
                    <div className="wo-mobile-card-header">
                      <span className="wo-job-number">{job.work_order_number}</span>
                      {renderPriorityBadge(job.priority)}
                    </div>
                    <div className="wo-mobile-card-body">
                      <div className="wo-mobile-customer">{job.customer}</div>
                      {job.invoice_number && (
                        <div className="wo-mobile-invoice">Invoice: {job.invoice_number}</div>
                      )}
                    </div>
                    <div className="wo-mobile-card-footer">
                      {renderStatusIndicator(job.status)}
                      <div className="wo-mobile-meta">
                        {job.due_date && (
                          <span className={`wo-mobile-due ${isOverdue(job) ? 'overdue' : ''}`}>
                            {formatDate(job.due_date)}
                          </span>
                        )}
                        <span className="wo-mobile-amount">{formatCurrency(job.total_due)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Work Order Modal */}
      <CreateWorkOrderModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingJob(null);
        }}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          setEditingJob(null);
          handleModalSuccess();
        }}
        editingJob={editingJob}
      />

      {/* Work Order Details Modal */}
      <WorkOrderDetailsModal
        job={selectedJob}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedJob(null);
        }}
        onSuccess={handleModalSuccess}
        onEdit={(job) => {
          setIsDetailsModalOpen(false);
          setSelectedJob(null);
          handleEditJob(job);
        }}
        canAdmin={canAdmin}
        statuses={WORK_ORDER_STATUSES}
      />
    </div>
  );
};

export default Dashboard;
